import { format } from 'date-fns';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { dogeIcon, mellIcon, pepeIcon, shibIcon } from '../../assets';
import ControlledInput from '../../components/Common/ControlledInput/ControlledInput';
import { transport } from '../../utils/constants';
import { getToken } from '../../utils/helper';
import './Calculator.scss';
import { CoinMarketCapClient } from '../../generated/mlmbox/client/asset/coin_market_cap.client';
import { Empty } from '../../generated/google/protobuf/empty';
import { Asset_MarketCap } from '../../generated/mlmbox/types/asset';

const formatDate = (date: number): string => format(new Date(date * 1000), 'dd.MM.yyyy');

enum FactorName {
    SHIB = 195,
    PEPE = 84,
    DOGE = 319,
}

interface IFactor {
    factor: number;
    icon: string;
    name: string;
}

interface IFactors {
    [key: number]: IFactor;
}

const factors: IFactors = {
    3: { factor: FactorName.SHIB, icon: shibIcon, name: 'Shiba Inu' },
    4: { factor: FactorName.PEPE, icon: pepeIcon, name: 'Pepe' },
    5: { factor: FactorName.DOGE, icon: dogeIcon, name: 'Dogecoin' },
};

const Calculator: FC = () => {
    const { t } = useTranslation();
    const [openModal, setOpenModal] = useState(false);
    const [amount, setAmount] = useState('');
    const [mellData, setMellData] = useState<Asset_MarketCap>();
    const [otherData, setOtherData] = useState<Asset_MarketCap[]>([]);

    const calcClient = new CoinMarketCapClient(transport);

    useEffect(() => {
        calcClient.all(Empty, { meta: { authorizatin: getToken() } }).then(({ status, response }) => {
            if (status.code === 'OK') {
                const otherResult: Asset_MarketCap[] = [];

                for (const market of response.items) {
                    if (market.assetId === 2) {
                        setMellData(market);
                        continue;
                    }
                    otherResult.push(market);
                }
                otherResult.sort((a, b) => Number(b.marketCap) - Number(a.marketCap));
                setOtherData(otherResult);
            }
        });
    }, []);

    return (
        <>
            <div className="calculator">
                <div className="calculator__header">
                    <h1>{t('calculator.title')}</h1>
                    <p>{t('calculator.description')}</p>
                </div>
                {mellData?.assetId && otherData.length > 0 ? (
                    <>
                        <div className="line"></div>
                        <div className="calculator__mell">
                            <h1>{t('calculator.title_2')}</h1>
                            {mellData?.modifiedAt && (
                                <>
                                    <div className="calculator__mell--info item-calc">
                                        <div className="item-calc__start">
                                            <img
                                                src={mellIcon}
                                                alt=""
                                            />
                                            <div className="item-calc__start--col">
                                                <h2>{mellData?.assetName}</h2>
                                                <p>{formatDate(mellData?.modifiedAt)}</p>
                                            </div>
                                        </div>
                                        <div className="item-calc__end">
                                            <div className="item-calc__end--col">
                                                <h2>${Math.round(Number(mellData.marketCap)).toLocaleString()}</h2>
                                                <p>Market Cap</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        onClick={() => setOpenModal(true)}
                                        className="calculator__mell--input">
                                        {amount.length > 0 ? `$${amount}` : t('calculator.input')}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="line"></div>
                        <div className="calculator__other">
                            <h1>{t('calculator.title_3')}</h1>

                            {otherData?.length > 0 && (
                                <>
                                    {otherData.map((data) => (
                                        <>
                                            <div
                                                key={data.assetId}
                                                className="calculator__other--info item-calc">
                                                <div className="item-calc__start">
                                                    <img
                                                        src={factors[data.assetId].icon}
                                                        alt=""
                                                    />
                                                    <div className="item-calc__start--col">
                                                        <h2>
                                                            {factors[data.assetId].name} <span>{`(${data.assetName})`}</span>
                                                        </h2>

                                                        <span>
                                                            Market Cap: <h2>${Math.round(Number(data.marketCap)).toLocaleString()}</h2>
                                                        </span>
                                                        <h2>
                                                            {t('calculator.win')}{' '}
                                                            <span className="green">
                                                                $
                                                                {Math.round(
                                                                    Number(amount) * (Number(data.marketCap) / Number(mellData?.marketCap))
                                                                ).toLocaleString()}
                                                            </span>
                                                        </h2>
                                                    </div>
                                                </div>
                                                <div className="factor">x{Math.round(Number(data.marketCap) / Number(mellData?.marketCap))}</div>
                                            </div>
                                        </>
                                    ))}
                                </>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="loading">
                        <div className="spinner small"></div>
                    </div>
                )}
            </div>
            {openModal && (
                <ControlledInput
                    close={() => setOpenModal(false)}
                    title={t('modals.ios.header.titles.calculator')}
                    typeInput="number"
                    placeholder={t('calculator.input')}
                    value={amount}
                    setValue={setAmount}
                />
            )}
        </>
    );
};

export default Calculator;
